/**
 * Named constants over the generated Global Conversations enums (GLOBAL_CONVERSATIONS_SLICE2_SPEC.md §5.1).
 *
 * NSwag generates the C# enums of `Core.Shared/Conversations/ConversationEnums.cs` with numeric member names
 * (`ConversationScope._1` …), so code that wrote the member directly would not say what it means. These maps give each
 * member its C# meaning, the `EventEditScope._3` precedent (main-layout.component.ts): every value IS the generated
 * member, never a numeric literal, so a renumbered enum fails the build instead of drifting.
 *
 * The map keys match the overlay's own string unions where one exists (`AllTab`, `ProjectTab`, `ProjectView`, the
 * `ConvFilters` kinds, reasons, client kinds and time kinds), so a reducer can index a map with the union value:
 * `CONV_TAB[tab]`, `CONV_REASON[reason]`, `CONV_TIME[time.kind]`.
 */
import {
    ConversationClientKind,
    ConversationEntryKind,
    ConversationReadAction,
    ConversationReason,
    ConversationScope,
    ConversationTab,
    ConversationTimeRange,
    ConversationViewMode,
} from '@shared/service-proxies/service-proxies';

/** `ConversationScope`: the whole firm's conversations or one project's. */
export const CONV_SCOPE = {
    all: ConversationScope._1,
    project: ConversationScope._2,
} as const;

/** `ConversationTab`: Needs attention and All (All scope); All, Comments and Emails (project scope). */
export const CONV_TAB = {
    attention: ConversationTab._1,
    all: ConversationTab._2,
    comments: ConversationTab._3,
    emails: ConversationTab._4,
} as const;

/** `ConversationViewMode`: one time-ordered stream, or entries gathered under their task. */
export const CONV_VIEW = {
    chronological: ConversationViewMode._1,
    byTask: ConversationViewMode._2,
} as const;

/**
 * `ConversationEntryKind`: a task comment (1), a project update (C# `ProjectComment`, 2) or an email thread (3). Ids of
 * kinds 1 and 2 come from two tables and can collide, so an entry is always named by kind and id together.
 */
export const CONV_KIND = {
    taskComment: ConversationEntryKind._1,
    projectUpdate: ConversationEntryKind._2,
    email: ConversationEntryKind._3,
} as const;

/** `ConversationReason` ([Flags]): why an entry reached the user. `ReasonMask` is the OR of these bits. */
export const CONV_REASON = {
    none: ConversationReason._0,
    assigned: ConversationReason._1,
    mentioned: ConversationReason._2,
    replyToYou: ConversationReason._4,
    clientReplied: ConversationReason._8,
    watching: ConversationReason._16,
    contributor: ConversationReason._32,
} as const;

/**
 * The direct reasons (Assigned | Mentioned | ReplyToYou | ClientReplied = 15): an unread entry with one of these needs
 * the user, counts toward the badge and is never read by the ambient auto-read. Not a generated member, so derived.
 */
export const DIRECT_MASK: number =
    CONV_REASON.assigned | CONV_REASON.mentioned | CONV_REASON.replyToYou | CONV_REASON.clientReplied;

/** The ambient reasons (Watching | Contributor = 48): read in bulk by the ambient auto-read at open (Q2, SD2-06). */
export const AMBIENT_MASK: number = CONV_REASON.watching | CONV_REASON.contributor;

/** `ConversationClientKind`: the client of an entry is a company or a person. */
export const CONV_CLIENT_KIND = {
    company: ConversationClientKind._1,
    person: ConversationClientKind._2,
} as const;

/** `ConversationTimeRange`: the Time filter. `custom` sends `FromDate`/`ToDate`; every other value sends neither. */
export const CONV_TIME = {
    none: ConversationTimeRange._0,
    today: ConversationTimeRange._1,
    last7: ConversationTimeRange._2,
    last30: ConversationTimeRange._3,
    custom: ConversationTimeRange._4,
} as const;

/**
 * `ConversationReadAction`: the read-state write. The overlay sends `read`, `unread` and `readAll` through
 * `SetReadState`; `ambient`, `undo`, `taskPost` and `readTask` name the server's own logged actions. `seen` (8, slice 3
 * SD3-07) is the task modal's kind-2 dwell: it raises the seen watermark only, is written by the server for
 * `ProjectComment/MarkProjectCommentSeen` and is never accepted by `SetReadState`.
 */
export const CONV_READ_ACTION = {
    read: ConversationReadAction._1,
    unread: ConversationReadAction._2,
    readAll: ConversationReadAction._3,
    ambient: ConversationReadAction._4,
    undo: ConversationReadAction._5,
    taskPost: ConversationReadAction._6,
    readTask: ConversationReadAction._7,
    seen: ConversationReadAction._8,
} as const;

/**
 * Email surfaces (the Emails tab, the "Emails" content type, the "Client replied" reason, the "comments and emails"
 * copy) are shown: slice 4 builds email entries and turns them on here (GLOBAL_CONVERSATIONS_SLICE4_SPEC.md §7.1,
 * GC4-FE-FLIP-01; SD2-15 and OQ2-01 closed). It is no longer a standalone rollback lever (SD4-20, OQ4-18, R4-07): the
 * server returns email entries whatever this says, and the mapper, card rules and store render kind 3 regardless, so
 * setting it back to false only hides the email tab, filter options and copy while the badge still counts the email
 * entries. A rollback goes with the S4 SQL (spec §12.4). Kept for S4 and removed in S5 with the code it guards. Typed
 * `boolean`, not the literal `true`, so code guarded by it keeps its normal types.
 */
export const CONVERSATION_EMAIL_UI: boolean = true;
